import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { ArrowsClockwise, ArrowUp, ArrowDown, Lightning, Sun, Moon, Cloud } from "@phosphor-icons/react";


const MOVE_LABEL = {
    received:         { label: "Received",         cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
    issued:           { label: "Issued",           cls: "bg-amber-50 text-amber-800 border-amber-200" },
    wastage:          { label: "Wastage",          cls: "bg-rose-50 text-rose-700 border-rose-200" },
    adjustment:       { label: "Adjusted",         cls: "bg-slate-100 text-slate-700 border-slate-200" },
    return:           { label: "Return",           cls: "bg-violet-50 text-violet-700 border-violet-200" },
    // iter97.18 — new movement type carrying expected/actual/wastage cols
    production_usage: { label: "Production usage", cls: "bg-cyan-50 text-cyan-800 border-cyan-200" },
};

const SHIFT_META = {
    morning:   { Icon: Sun,   label: "Morning",   cls: "text-amber-600" },
    afternoon: { Icon: Cloud, label: "Afternoon", cls: "text-cyan-600" },
    night:     { Icon: Moon,  label: "Night",     cls: "text-indigo-600" },
};

const fmtDT = (s) => s
    ? new Date(s).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })
    : "—";


export function MovementsTab() {
    const [rows, setRows] = useState([]);
    const [type, setType] = useState("all");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        const params = type === "all" ? {} : { movement_type: type };
        api.get("/admin/inventory/movements", { params })
            .then((r) => setRows(r.data || []))
            .finally(() => setLoading(false));
    }, [type]);

    return (
        <div className="space-y-4" data-testid="movements-tab">
            <div className="flex items-center gap-2 overflow-x-auto bg-card border border-border rounded-full p-0.5 w-fit">
                {["all", "production_usage", "received", "issued", "wastage", "adjustment", "return"].map((t) => (
                    <button
                        key={t}
                        type="button"
                        onClick={() => setType(t)}
                        className={`px-3 h-9 rounded-full text-[11px] font-semibold whitespace-nowrap transition ${type === t ? "bg-foreground text-white" : "text-muted-foreground hover:text-foreground"}`}
                        data-testid={`movements-filter-${t}`}
                    >
                        {t === "all" ? "All" : MOVE_LABEL[t]?.label}
                    </button>
                ))}
            </div>

            <div className="rounded-2xl border border-border bg-card overflow-hidden">
                <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-3 text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground border-b border-border bg-secondary/30">
                    <div className="col-span-2">When</div>
                    <div className="col-span-2">Material</div>
                    <div className="col-span-2">Type / Case</div>
                    <div className="col-span-1 text-right">Exp</div>
                    <div className="col-span-1 text-right">Actual</div>
                    <div className="col-span-1 text-right">Waste</div>
                    <div className="col-span-1 text-center">Shift</div>
                    <div className="col-span-2">Actor / Reason</div>
                </div>

                {loading ? (
                    <div className="py-10 text-center text-muted-foreground text-sm">Loading…</div>
                ) : rows.length === 0 ? (
                    <div className="py-10 text-center text-muted-foreground text-sm" data-testid="movements-empty">No movements yet.</div>
                ) : rows.map((r) => {
                    const t = MOVE_LABEL[r.movement_type] || MOVE_LABEL.adjustment;
                    const positive = (r.delta || 0) >= 0;
                    const isProdUsage = r.movement_type === "production_usage";
                    const sev = r.wastage_severity;
                    const wastageCellCls = sev === "high"
                        ? "text-rose-700 font-bold bg-rose-50/60 rounded"
                        : sev === "moderate"
                            ? "text-amber-700 font-semibold bg-amber-50/60 rounded"
                            : "text-muted-foreground";
                    const shift = SHIFT_META[r.shift];
                    return (
                        <div
                            key={r.id}
                            className="grid grid-cols-1 md:grid-cols-12 gap-2 px-4 py-3 border-b border-border last:border-0 items-center text-sm"
                            data-testid={`movement-row-${r.id}`}
                        >
                            <div className="col-span-2 text-xs text-muted-foreground tabular-nums">{fmtDT(r.created_at)}</div>
                            <div className="col-span-2 min-w-0">
                                <div className="font-mono text-[10px] text-muted-foreground">{r.material_sku}</div>
                                <div className="text-xs truncate">{r.material_name}</div>
                            </div>
                            <div className="col-span-2 min-w-0">
                                <span className={`text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 rounded border ${t.cls}`}>{t.label}</span>
                                {isProdUsage && r.case_code && (
                                    <div className="text-[10px] text-muted-foreground font-mono mt-0.5 truncate" title={r.case_code}>
                                        {r.case_code}
                                    </div>
                                )}
                                {!isProdUsage && r.po_code && (
                                    <span className="text-[10px] text-muted-foreground ml-1 font-mono">{r.po_code}</span>
                                )}
                            </div>
                            {/* Expected · Actual · Wastage — only meaningful for production_usage.
                                For other types we render Δ in the Actual column and dash the others. */}
                            <div className="col-span-1 text-right text-xs tabular-nums">
                                {isProdUsage && r.expected_qty != null ? (
                                    <span className="text-muted-foreground">{Number(r.expected_qty).toLocaleString("en-IN")}</span>
                                ) : (
                                    <span className="text-muted-foreground/50">—</span>
                                )}
                            </div>
                            <div className={`col-span-1 text-right tabular-nums text-xs font-semibold ${positive ? "text-emerald-700" : "text-foreground"}`}>
                                {isProdUsage && r.actual_qty != null
                                    ? Number(r.actual_qty).toLocaleString("en-IN")
                                    : (
                                        <span className="inline-flex items-center gap-0.5 justify-end w-full">
                                            {positive ? <ArrowUp size={9} weight="bold" /> : <ArrowDown size={9} weight="bold" className="text-rose-700" />}
                                            <span className={positive ? "" : "text-rose-700"}>{Math.abs(r.delta || 0).toLocaleString()}</span>
                                        </span>
                                    )}
                            </div>
                            <div className={`col-span-1 text-right tabular-nums text-xs px-1 ${wastageCellCls}`} data-testid={`movement-waste-${r.id}`}>
                                {isProdUsage && r.wastage_qty != null ? (
                                    <>
                                        {r.wastage_qty > 0 ? `+${Number(r.wastage_qty).toLocaleString("en-IN")}` : "0"}
                                        {sev === "high" && <Lightning size={9} weight="fill" className="inline ml-0.5 text-rose-600" />}
                                    </>
                                ) : (
                                    <span className="text-muted-foreground/50">—</span>
                                )}
                            </div>
                            <div className="col-span-1 text-center">
                                {shift ? (
                                    <span className={`inline-flex items-center gap-0.5 text-[10px] ${shift.cls}`} title={shift.label}>
                                        <shift.Icon size={11} weight="fill" />
                                    </span>
                                ) : <span className="text-muted-foreground/50 text-xs">—</span>}
                            </div>
                            <div className="col-span-2 text-xs text-muted-foreground truncate" title={r.reason}>
                                <div className="truncate">{r.tech_user_name || r.created_by_name || "—"}</div>
                                {r.wastage_reason && (
                                    <div className="text-[10px] text-rose-600 truncate" title={r.wastage_reason}>
                                        {r.wastage_reason.replaceAll("_", " ")}
                                    </div>
                                )}
                                {!r.wastage_reason && r.reason && (
                                    <div className="text-[10px] truncate italic">{r.reason}</div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
